/**
 * Minimum lead time for student-initiated private bookings.
 * When `timeZone` is set (IANA), session start is interpreted in that zone; otherwise
 * uses the runtime default (legacy behavior).
 */

import { toDate } from 'date-fns-tz';
import type { Academy } from '../types';

function normalizeStartTime(time: string): string {
  const t = time.trim();
  if (t.length >= 5) return t.slice(0, 5);
  return t;
}

/**
 * Default IANA zone from academy region for lead-time comparisons (server may run in UTC).
 * Not a substitute for a stored per-academy timezone; expands later if we persist one.
 */
export function resolveLeadTimeZoneForAcademy(academy: Pick<Academy, 'region'>): string | undefined {
  if (academy.region === 'AU') return 'Australia/Sydney';
  if (academy.region === 'US') return 'America/New_York';
  return undefined;
}

/**
 * True if the session start is at least `hours` after `now` (strictly in the future by that margin).
 */
export function isSessionStartAtLeastHoursAhead(
  sessionDateYmd: string,
  startTime: string,
  hours: number,
  now: Date = new Date(),
  timeZone?: string
): boolean {
  if (hours <= 0) return true;
  const hm = normalizeStartTime(startTime);
  let sessionMs: number;
  if (timeZone) {
    try {
      sessionMs = toDate(`${sessionDateYmd}T${hm}:00`, { timeZone }).getTime();
    } catch {
      sessionMs = new Date(`${sessionDateYmd}T${hm}:00`).getTime();
    }
  } else {
    sessionMs = new Date(`${sessionDateYmd}T${hm}:00`).getTime();
  }
  if (Number.isNaN(sessionMs)) return false;
  const cutoffMs = now.getTime() + hours * 60 * 60 * 1000;
  return sessionMs >= cutoffMs;
}
